import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, Clock, Layers, ShieldCheck, Camera, PenLine, Trophy } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

export function PackCard({ p }) {
  return (
    <Link to={`/tests/${p.slug}`} className="group bg-white border border-border rounded-xl p-5 flex flex-col hover:border-crimson/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200" data-testid={`pack-card-${p.slug}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-crimson">{p.exam_name}</span>
        {p.price > 0 ? <span className="font-heading font-black text-navy">₹{p.price}{p.mrp > p.price && <s className="ml-1 text-xs text-muted-foreground font-normal">₹{p.mrp}</s>}</span>
          : <span className="text-[11px] font-bold bg-emerald-50 text-emerald-700 rounded px-2 py-0.5">FREE</span>}
      </div>
      <h3 className="font-heading font-bold text-navy mt-2 leading-snug group-hover:text-crimson transition-colors">{p.title}</h3>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><FileText size={13} /> {p.test_count} Tests</span>
        {p.free_tests > 0 && p.price > 0 && <span className="text-emerald-700 font-medium">{p.free_tests} Free</span>}
        <span className="flex items-center gap-1"><Clock size={13} /> {p.validity_days} days</span>
      </div>
      <div className="mt-auto pt-4 text-xs text-muted-foreground line-clamp-1">{(p.languages || []).join(", ")}</div>
      <div className="mt-3 h-9 rounded-md bg-navy text-white text-sm font-semibold flex items-center justify-center group-hover:bg-crimson transition-colors">View Test Series</div>
    </Link>
  );
}

function Hero({ q, setQ, counts }) {
  return (
    <section className="bg-navy text-white relative overflow-hidden" data-testid="ts-hero">
      <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-crimson/25 blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 py-14 relative">
        <p className="text-crimson font-semibold tracking-widest text-xs uppercase">VIRIKS Test Series</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black mt-3 max-w-3xl leading-tight">Real exam-style mock tests for every exam you are preparing for</h1>
        <p className="text-white/70 mt-4 max-w-2xl text-sm md:text-base">CTET, STET, Class 8–12, SSC, Banking, Railways and more — objective and written questions, live proctoring and all-India rank.</p>
        <div className="mt-7 max-w-xl relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exam, subject or test series" className="h-12 pl-11 bg-white text-navy rounded-full" data-testid="ts-search" />
        </div>
        <div className="flex flex-wrap gap-6 mt-8 text-sm">
          {[[Layers, `${counts.exams}+ exams`], [FileText, `${counts.packs} test series`], [Camera, "Camera proctored"], [PenLine, "Objective + written"], [Trophy, "All-India rank"]].map(([I, t]) => (
            <span key={t} className="flex items-center gap-2 text-white/80"><I size={16} className="text-crimson" />{t}</span>))}
        </div>
      </div>
    </section>
  );
}

export default function Tests() {
  const [exams, setExams] = useState([]);
  const [packs, setPacks] = useState([]);
  const [group, setGroup] = useState("");
  const [exam, setExam] = useState("");
  const [price, setPrice] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/testseries/exams").then(({ data }) => setExams(data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/testseries/packs", { params: { exam, group: exam ? "" : group, q, free: price } }).then(({ data }) => setPacks(data)), 250);
    return () => clearTimeout(t);
  }, [exam, group, q, price]);
  const groups = useMemo(() => [...new Set(exams.map((e) => e.group))], [exams]);
  const shown = exams.filter((e) => !group || e.group === group);
  const chip = (on) => `px-4 py-2 rounded-full text-sm font-medium border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white border-border hover:border-navy/40"}`;

  return (
    <div data-testid="tests-page">
      <Hero q={q} setQ={setQ} counts={{ exams: exams.length, packs: packs.length }} />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="ts-groups">
          <button className={chip(!group)} onClick={() => { setGroup(""); setExam(""); }} data-testid="ts-group-all">All Exams</button>
          {groups.map((g) => <button key={g} className={chip(group === g)} onClick={() => { setGroup(g); setExam(""); }} data-testid={`ts-group-${g.toLowerCase().replace(/\s+/g, "-")}`}>{g}</button>)}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {shown.map((e) => <button key={e.slug} onClick={() => setExam(exam === e.slug ? "" : e.slug)} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${exam === e.slug ? "border-crimson text-crimson bg-crimson/5" : "border-border text-navy/70 hover:text-navy"}`} data-testid={`ts-exam-${e.slug}`}>{e.name}</button>)}
        </div>
        <div className="flex items-center justify-between mt-8 mb-4">
          <h2 className="font-heading font-bold text-navy text-base md:text-lg">{exam ? exams.find((e) => e.slug === exam)?.name : group || "All"} Test Series <span className="text-muted-foreground font-normal">({packs.length})</span></h2>
          <div className="flex gap-1 bg-muted rounded-lg p-1 text-xs">
            {[["", "All"], ["1", "Free"], ["0", "Paid"]].map(([v, l]) => <button key={l} onClick={() => setPrice(v)} className={`px-3 py-1.5 rounded-md ${price === v ? "bg-white shadow-sm font-semibold" : ""}`} data-testid={`ts-price-${l.toLowerCase()}`}>{l}</button>)}
          </div>
        </div>
        {packs.length === 0 ? <div className="text-center py-16 text-muted-foreground" data-testid="ts-empty">No test series found.</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{packs.map((p) => <PackCard key={p.id} p={p} />)}</div>
        )}
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {[[ShieldCheck, "Fair, proctored exams", "Your camera stays on during the test. Leaving the screen, another person or switching tabs ends the test."], [PenLine, "Written answers checked", "Written answers get an AI-suggested score which a VIRIKS evaluator confirms."], [Trophy, "Know where you stand", "Rank, percentile, accuracy and question-wise solutions after every test."]].map(([I, t, d]) => (
            <div key={t} className="border border-border rounded-xl p-6 bg-white"><I className="text-crimson" size={22} /><h3 className="font-heading font-bold text-navy mt-3">{t}</h3><p className="text-sm text-muted-foreground mt-1">{d}</p></div>))}
        </div>
      </div>
    </div>
  );
}
